import { useEffect, useId, useRef, useState } from 'react';
import type { JsonObject } from '@inf-monkeys-tech/monkeys';
import type { DesignCapabilityRendererContext } from './contracts';

export interface TransientFileMetadata { handleId: string; fileName: string; size: number; mimeType: string }
export interface TransientFileRegistry {
  register(file: File, options?: { maxBytes: number }): TransientFileMetadata;
  discard(handleId: string): void;
}
export interface FilePickerContext extends DesignCapabilityRendererContext { transientFiles?: TransientFileRegistry }
export const FILE_PICKER_MAX_BYTES = 5 * 1024 * 1024 * 1024;
export const FILE_PICKER_SCHEMA = 'monkeys-design://render-model/file-picker/v1';
const metadataProperties: JsonObject = {
  handleId: { type: 'string', minLength: 1, maxLength: 256 },
  fileName: { type: 'string', minLength: 1, maxLength: 1024 },
  size: { type: 'integer', minimum: 0, maximum: FILE_PICKER_MAX_BYTES },
  mimeType: { type: 'string', maxLength: 256 },
};
const metadataSchema: JsonObject = { type: 'object', additionalProperties: false, required: ['handleId', 'fileName', 'size', 'mimeType'], properties: metadataProperties };
export const FILE_PICKER_RENDER_MODEL_SCHEMA: JsonObject = {
  $schema: 'https://json-schema.org/draft/2020-12/schema', $id: FILE_PICKER_SCHEMA,
  type: 'object', additionalProperties: false, required: ['labelKey', 'maxBytes'],
  properties: {
    labelKey: { type: 'string', minLength: 1, maxLength: 256 },
    accept: { type: 'string', maxLength: 1024 },
    maxBytes: { type: 'integer', minimum: 1, maximum: FILE_PICKER_MAX_BYTES },
    disabled: { type: 'boolean' }, submitting: { type: 'boolean' }, selected: { anyOf: [{ type: 'null' }, metadataSchema] },
  },
};
export const FILE_PICKER_SELECT_INTENT_SCHEMA: JsonObject = {
  $schema: 'https://json-schema.org/draft/2020-12/schema', $id: 'monkeys-design://intent/file-picker-select/v1', ...metadataSchema,
};
export const FILE_PICKER_CANCEL_INTENT_SCHEMA: JsonObject = {
  $schema: 'https://json-schema.org/draft/2020-12/schema', $id: 'monkeys-design://intent/file-picker-cancel/v1', type: 'object', additionalProperties: false, maxProperties: 0,
};
export function filePickerAccepts(file: Pick<File, 'name' | 'type'>, accept: string | undefined): boolean {
  if (!accept?.trim()) return true;
  const name = file.name.toLowerCase(), type = file.type.toLowerCase();
  return accept.split(',').some(raw => {
    const rule = raw.trim().toLowerCase();
    if (/^\.[a-z0-9][a-z0-9._-]*$/.test(rule)) return name.endsWith(rule);
    if (/^[a-z0-9!#$&^_.+-]+\/\*$/.test(rule)) return type.startsWith(rule.slice(0, -1));
    return /^[a-z0-9!#$&^_.+-]+\/[a-z0-9!#$&^_.+-]+$/.test(rule) && type === rule;
  });
}
export function readTransientFileMetadata(value: unknown): TransientFileMetadata | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const object = value as Record<string, unknown>;
  if (Object.keys(object).length !== 4 || typeof object.handleId !== 'string' || object.handleId.length < 1 || object.handleId.length > 256 ||
      typeof object.fileName !== 'string' || object.fileName.length < 1 || object.fileName.length > 1024 ||
      typeof object.mimeType !== 'string' || object.mimeType.length > 256 ||
      typeof object.size !== 'number' || !Number.isInteger(object.size) || object.size < 0 || object.size > FILE_PICKER_MAX_BYTES) return undefined;
  return { handleId: object.handleId, fileName: object.fileName, size: object.size, mimeType: object.mimeType };
}
function FilePickerView({ model, translate, emitIntent, transientFiles }: FilePickerContext) {
  const id = useId(), input = useRef<HTMLInputElement>(null);
  const current = useRef<TransientFileMetadata>();
  const [selected, setSelected] = useState<TransientFileMetadata>();
  const [error, setError] = useState<string>();
  const configured = typeof model.labelKey === 'string' && typeof model.maxBytes === 'number' && Number.isInteger(model.maxBytes) && model.maxBytes >= 1 && model.maxBytes <= FILE_PICKER_MAX_BYTES;
  const disabled = !configured || !transientFiles || model.disabled === true || model.submitting === true;
  const authoritative = readTransientFileMetadata(model.selected);
  const authoritativeKey = authoritative ? JSON.stringify(authoritative) : '';
  useEffect(() => () => {
    if (current.current) transientFiles?.discard(current.current.handleId);
    current.current = undefined;
  }, [transientFiles]);
  useEffect(() => {
    const next = transientFiles ? authoritative : undefined;
    if (current.current && current.current.handleId !== next?.handleId) transientFiles?.discard(current.current.handleId);
    current.current = next; setSelected(next); setError(undefined);
    if (input.current) input.current.value = '';
    // Scalar identity avoids clearing a local selection when the same model is re-rendered.
  }, [transientFiles, authoritativeKey]);
  const clear = () => {
    if (disabled) return;
    if (current.current) transientFiles?.discard(current.current.handleId);
    current.current = undefined; setSelected(undefined); setError(undefined);
    if (input.current) input.current.value = '';
    emitIntent('capability.file-picker.cancel', {});
  };
  const label = typeof model.labelKey === 'string' ? model.labelKey : 'monkeys.design.capability.filePicker.label';
  return <div className="grid gap-2">
    <label htmlFor={id}>{translate(label)}</label>
    <input ref={input} id={id} type="file" accept={typeof model.accept === 'string' ? model.accept : undefined} disabled={disabled} aria-invalid={Boolean(error)} onChange={event => {
      if (disabled || !transientFiles) return;
      const file = event.currentTarget.files?.[0];
      // Clear the native selection so retries of the same file produce another change event.
      event.currentTarget.value = '';
      if (!file) return;
      if (file.size > Number(model.maxBytes)) { setError('monkeys.design.capability.filePicker.tooLarge'); return; }
      if (!filePickerAccepts(file, typeof model.accept === 'string' ? model.accept : undefined)) { setError('monkeys.design.capability.filePicker.invalidType'); return; }
      let registered: TransientFileMetadata;
      try { registered = transientFiles.register(file, { maxBytes: Number(model.maxBytes) }); }
      catch { setError('monkeys.design.capability.filePicker.registrationFailed'); return; }
      // Select named scalar fields; host objects and file contents never enter an intent.
      const metadata = { handleId: registered.handleId, fileName: registered.fileName, size: registered.size, mimeType: registered.mimeType };
      const previous = current.current;
      current.current = metadata; setSelected(metadata); setError(undefined);
      if (previous && previous.handleId !== metadata.handleId) transientFiles.discard(previous.handleId);
      emitIntent('capability.file-picker.select', metadata);
    }} />
    {selected ? <span>{selected.fileName}</span> : null}
    {error ? <span role="alert">{translate(error)}</span> : null}
    <button type="button" disabled={disabled} onClick={clear}>{translate('monkeys.design.capability.filePicker.cancel')}</button>
  </div>;
}
export const FilePickerCapabilityRenderer = (context: FilePickerContext) => <FilePickerView {...context} />;
